import { AnalyticsListener } from '@atlaskit/analytics-next';
import AkButton from '@atlaskit/button/standard-button';
import { FacetType, SearchParamsUpdate } from '@atlassian/amkt-addon-discovery';
import { H3 } from '@atlassian/amkt-common';
import { MarketplaceAnalyticsContext } from '@atlassian/mpac-analytics';
import { withFeatureFlags } from '@atlassian/mpac-ui';
import { LinkUtils, UrlUtils } from '@atlassian/mpac-utils';
import { Map, Set } from 'immutable';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import Meta from '../../../../../../../../../common/components/Meta';
import ServerErrorSpinnerMask from '../../../../../../../../../common/components/ServerErrorSpinnerMask';
import * as analytics from '../../../../../../../../../common/utils/analytics/analytics';
import CommonPropTypes from '../../../../../../../../constants/PropTypes';
import { hasJustCompletedLoading } from '../../../../../../../../utils/BrowserMetrics';
import CallToActionBanner from '../../../../../../../common/CallToActionBanner';
import {
  navigateToAddon,
  resolveHitHref,
  resolveHitImageHref,
  resolveHostingFromHit,
} from '../../../../../../utils/AddonDiscovery';
import { resolvePathAndQuery } from '../../../../../AddonDiscoveryRoutes/components/AddonDiscoveryWrapper/utils/links';
import * as perspectives from '../../../../../AddonDiscoveryRoutes/constants/perspectives';
import { CATEGORY_NOT_FOUND_ERROR_MESSAGE } from '../../../../actions/Solutions';
import SolutionsPropTypes from '../../../../constants/SolutionsPropTypes';
import ContentSeparator from '../ContentSeparator';
import SolutionAddons from '../SolutionAddons';
import SolutionAtlassianProductDiversion from '../SolutionAtlassianProductDiversion';
import SolutionBodyBottomSection from '../SolutionBodyBottomSection';
import SolutionBodyTopSection from '../SolutionBodyTopSection';
import SolutionHeading from '../SolutionHeading';

import styles from '../Solution.less';
import OrganizeWorkflowImage from '../../../../config/images/organize-workflow.svg';

const MAX_LARGE_CURATED_APP_COUNT = 6;
const MAX_SMALL_CURATED_APP_COUNT = 8;
const MAX_ALL_CURATED_APP_COUNT = MAX_LARGE_CURATED_APP_COUNT + MAX_SMALL_CURATED_APP_COUNT;
const SMALL_CURATED_APPS_PER_ROW = 4;

const emptyFilters = Map();

function resolveCuratedApps(solutionContent: any) {
  const solutionContentData = solutionContent.get('data');
  return solutionContentData
    ? solutionContentData.curatedApps.resolveItems(MAX_ALL_CURATED_APP_COUNT)
    : undefined;
}

/*
  Math:
  numAppsForFullRows(2, 4) = 0
  numAppsForFullRows(3, 4) = 0
  numAppsForFullRows(7, 4) = 4
  numAppsForFullRows(8, 4) = 8
 */
function numAppsForFullRows(totalAppCount: any, appsPerRow: any) {
  return Math.floor(totalAppCount / appsPerRow) * appsPerRow;
}

class Solution extends Component {
  static propTypes = {
    addonDiscoveryConfig: CommonPropTypes.addonDiscoveryConfig.isRequired,
    commonConfig: CommonPropTypes.commonConfig.isRequired,
    config: CommonPropTypes.solutionsConfig.isRequired,
    isCuratedAddonsRenderComplete: PropTypes.bool.isRequired,
    generalLinks: CommonPropTypes.generalLinks.isRequired,
    history: CommonPropTypes.history.isRequired,
    onLargeCuratedAddonsRenderComplete: PropTypes.func.isRequired,
    onLargeCuratedAddonsRenderStarted: PropTypes.func.isRequired,
    onSmallCuratedAddonsRenderComplete: PropTypes.func.isRequired,
    onSmallCuratedAddonsRenderStarted: PropTypes.func.isRequired,
    onLoadingComplete: PropTypes.func.isRequired,
    onLoadingStart: PropTypes.func.isRequired,
    retrieveSolutionContent: PropTypes.func.isRequired,
    solutionKey: PropTypes.string.isRequired,
    solutionContent: CommonPropTypes.asyncData(SolutionsPropTypes.solutionContent).isRequired,
    enableJsdToJsm: PropTypes.bool,
  };

  constructor(props: any) {
    super(props);

    // @ts-expect-error [MC-2850] - TS2339 - Property 'baseHitContext' does not exist on type 'Solution'.
    this.baseHitContext = Map({
      onHitClick: this.onHitClick,
      hitHrefResolver: this.hitHrefResolver,
      hitImageHrefResolver: this.hitImageHrefResolver,
    });

    // @ts-expect-error [MC-2850] - TS2339 - Property 'largeCuratedAppsTransitions' does not exist on type 'Solution'.
    this.largeCuratedAppsTransitions = Map({
      // @ts-expect-error [MC-2850] - TS2339 - Property 'onLargeCuratedAddonsRenderComplete' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
      transitionCompleted: this.props.onLargeCuratedAddonsRenderComplete,
      // @ts-expect-error [MC-2850] - TS2339 - Property 'onLargeCuratedAddonsRenderStarted' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
      transitionStarted: this.props.onLargeCuratedAddonsRenderStarted,
    });

    // @ts-expect-error [MC-2850] - TS2339 - Property 'smallCuratedAppsTransitions' does not exist on type 'Solution'.
    this.smallCuratedAppsTransitions = Map({
      // @ts-expect-error [MC-2850] - TS2339 - Property 'onSmallCuratedAddonsRenderComplete' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
      transitionCompleted: this.props.onSmallCuratedAddonsRenderComplete,
      // @ts-expect-error [MC-2850] - TS2339 - Property 'onSmallCuratedAddonsRenderStarted' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
      transitionStarted: this.props.onSmallCuratedAddonsRenderStarted,
    });

    // @ts-expect-error [MC-2850] - TS2339 - Property 'largeCuratedAppsApplyOverrides' does not exist on type 'Solution'.
    this.largeCuratedAppsApplyOverrides = new SearchParamsUpdate({
      // @ts-expect-error [MC-2850] - TS2322 - Type 'Set<FacetType>' is not assignable to type 'undefined'.
      types: Set([
        // @ts-expect-error [MC-2850] - TS2322 - Type 'string' is not assignable to type 'undefined'. | TS2322 - Type 'string' is not assignable to type 'undefined'.
        new FacetType({ algoliaFacetKey: 'addonKey', queryType: 'disjunctive' }),
      ]),
      // @ts-expect-error [MC-2850] - TS2339 - Property 'addLargeCuratedAddonsToStaticSearchFilters' does not exist on type 'Solution'.
      apply: this.addLargeCuratedAddonsToStaticSearchFilters,
    });

    // @ts-expect-error [MC-2850] - TS2339 - Property 'smallCuratedAppsApplyOverrides' does not exist on type 'Solution'.
    this.smallCuratedAppsApplyOverrides = new SearchParamsUpdate({
      // @ts-expect-error [MC-2850] - TS2322 - Type 'Set<FacetType>' is not assignable to type 'undefined'.
      types: Set([
        // @ts-expect-error [MC-2850] - TS2322 - Type 'string' is not assignable to type 'undefined'. | TS2322 - Type 'string' is not assignable to type 'undefined'.
        new FacetType({ algoliaFacetKey: 'addonKey', queryType: 'disjunctive' }),
      ]),
      // @ts-expect-error [MC-2850] - TS2339 - Property 'addSmallCuratedAddonsToStaticSearchFilters' does not exist on type 'Solution'.
      apply: this.addSmallCuratedAddonsToStaticSearchFilters,
    });
  }

  state = {
    // @ts-expect-error [MC-2850] - TS2339 - Property 'solutionContent' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
    curatedApps: resolveCuratedApps(this.props.solutionContent),
    // @ts-expect-error [MC-2850] - TS2339 - Property 'solutionContent' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
    solutionContent: this.props.solutionContent,
  };

  getMetricsKey() {
    // @ts-expect-error [MC-2850] - TS2339 - Property 'config' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
    return this.props.config.metricsKeys.solution;
  }

  componentDidMount() {
    // @ts-expect-error [MC-2850] - TS2339 - Property 'retrieveSolutionContent' does not exist on type 'Solution'. | TS2339 - Property 'solutionKey' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
    this.retrieveSolutionContent({ solutionKey: this.props.solutionKey });
  }

  static getDerivedStateFromProps(nextProps: any, prevState: any) {
    if (nextProps.solutionContent !== prevState.solutionContent) {
      return {
        curatedApps: resolveCuratedApps(nextProps.solutionContent),
        solutionContent: nextProps.solutionContent,
      };
    } else return null;
  }

  componentDidUpdate(prevProps: any) {
    // @ts-expect-error [MC-2850] - TS2339 - Property 'solutionKey' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
    if (prevProps.solutionKey !== this.props.solutionKey) {
      // @ts-expect-error [MC-2850] - TS2339 - Property 'retrieveSolutionContent' does not exist on type 'Solution'. | TS2339 - Property 'solutionKey' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
      this.retrieveSolutionContent({ solutionKey: this.props.solutionKey });
    }
    if (hasJustCompletedLoading(prevProps, this.props, this.isLoading)) {
      // @ts-expect-error [MC-2850] - TS2339 - Property 'onLoadingComplete' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
      this.props.onLoadingComplete({ metricsKey: this.getMetricsKey() });
    }
  }

  isLoading = (props: any) =>
    !props.isCuratedAddonsRenderComplete || props.solutionContent.get('loading');

  getErrorStatusCode = () => {
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    const messages = this.props.solutionContent.get('dataErrorMessages').toJS();
    if (messages.some((error) => error.message === CATEGORY_NOT_FOUND_ERROR_MESSAGE)) {
      return 404;
    }
    return 502;
  };

  retrieveSolutionContent({ solutionKey }) {
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
    this.props.onLoadingStart({ metricsKey: this.getMetricsKey() });
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'. | TS18004 - No value exists in scope for the shorthand property 'solutionKey'. Either declare one or provide an initializer.
    this.props.retrieveSolutionContent({ solutionKey });
  }

  render() {
    return (
      <ServerErrorSpinnerMask
        // @ts-expect-error [MC-2850]
        homeLink={this.props.addonDiscoveryConfig.links.home}
        // @ts-expect-error [MC-2850]
        history={this.props.history}
        // @ts-expect-error [MC-2850]
        isError={this.props.solutionContent.get('dataError')}
        // @ts-expect-error [MC-2850]
        loading={this.props.solutionContent.get('loading')}
        statusCode={this.getErrorStatusCode()}
      >
        {
          // @ts-expect-error [MC-2850]
          () => (this.props.solutionContent.get('data') ? this.resolveSolutionContent() : null)
        }
      </ServerErrorSpinnerMask>
    );
  }

  // eslint-disable-next-line max-lines-per-function -- Fix me
  resolveSolutionContent() {
    const seeMoreHref = this.resolveSeeMoreHref();
    // @ts-expect-error [MC-2850] - TS6133 - 'content' is declared but its value is never read. | TS2532 - Object is possibly 'undefined'.
    const content = this.props.solutionContent.get('data');

    return (
      <MarketplaceAnalyticsContext
        attributes={{
          gaValueKey: ['attributes', 'hitRank'],
          gaCategory: analytics.SOLUTIONS_CATEGORY,
        }}
      >
        <div className={styles.solution}>
          {/* @ts-expect-error [MC-2850] */}
          <Meta
            description={content.pageMetaDetails.description}
            image={content.heroImage.png}
            title={content.pageMetaDetails.title}
          />
          <SolutionHeading
            heading={content.heading}
            image={content.heroImage.svg}
            category={content.name}
          />
          <SolutionBodyTopSection
            // @ts-expect-error [MC-2850]
            addonDiscoveryConfig={this.props.addonDiscoveryConfig}
            breadcrumbText={content.name}
            // @ts-expect-error [MC-2850]
            history={this.props.history}
            path={UrlUtils.resolveUrlFromTemplateAndParameters({
              // @ts-expect-error [MC-2850]
              template: this.props.config.links.solution.href,
              // @ts-expect-error [MC-2850]
              parameters: { category: this.props.solutionKey },
            })}
            primaryDescription={content.primaryDescription}
            tagline={content.tagline}
          />
          {content.appsSectionTitle && (
            <div className={styles.solutionTopContainer}>
              <ContentSeparator />
              <H3 className={styles.appsSectionTitle}>{content.appsSectionTitle}</H3>
            </div>
          )}
          <div className={styles.solutionLargeHitsSection}>
            <AnalyticsListener channel="addonDiscovery" onEvent={this.updateLargeLinkSubjectID}>
              <SolutionAddons
                // @ts-expect-error [MC-2850]
                algoliaConfig={this.props.commonConfig.algoliaConfig}
                // @ts-ignore MC-2850
                applyOverrides={this.largeCuratedAppsApplyOverrides}
                // @ts-ignore MC-2850
                environment={this.props.commonConfig.environment}
                filters={emptyFilters}
                hidePagination={true}
                // @ts-ignore MC-2850
                hitContext={this.baseHitContext.merge({ onHitMount: this.onLargeCuratedHitMount })}
                hitSize="large"
                maxLength={MAX_LARGE_CURATED_APP_COUNT}
                sortHits={this.sortHits}
                // @ts-ignore MC-2850
                transitions={this.largeCuratedAppsTransitions}
              />
            </AnalyticsListener>
          </div>
          <div className={styles.solutionSmallHitsSection}>
            <AnalyticsListener channel="addonDiscovery" onEvent={this.updateSmallLinkSubjectID}>
              {this.numSmallCuratedAppsForDisplay() !== 0 && (
                <SolutionAddons
                  // @ts-ignore MC-2850
                  algoliaConfig={this.props.commonConfig.algoliaConfig}
                  // @ts-ignore MC-2850
                  applyOverrides={this.smallCuratedAppsApplyOverrides}
                  // @ts-ignore MC-2850
                  environment={this.props.commonConfig.environment}
                  filters={emptyFilters}
                  hidePagination={true}
                  // @ts-ignore MC-2850
                  hitContext={this.baseHitContext.merge({
                    onHitMount: this.onSmallCuratedHitMount,
                  })}
                  hitSize="small"
                  maxLength={MAX_SMALL_CURATED_APP_COUNT}
                  sortHits={this.sortHits}
                  // @ts-expect-error [MC-2850]
                  transitions={this.smallCuratedAppsTransitions}
                />
              )}
            </AnalyticsListener>
          </div>
          <div className={styles.exploreMoreButtonSection}>
            <AkButton
              appearance="primary"
              className={styles.exploreMoreButton}
              href={seeMoreHref}
              onClick={this.onSeeMoreClick}
            >
              {content.viewMoreActionText || 'Explore all'}
            </AkButton>
          </div>
          {content.atlassianProductDiversion ? (
            <div className={styles.atlassianProductDiversionContainer}>
              <SolutionAtlassianProductDiversion
                atlassianProductDiversion={content.atlassianProductDiversion}
              />
            </div>
          ) : null}
          <div className={styles.sectionsContainer}>
            <SolutionBodyBottomSection
              coloredBackground={true}
              heading={content.moreDetails.heading}
              image={content.moreDetails.icon}
              paragraph={
                <span>
                  {content.moreDetails.content.map((p, i) => (
                    <p key={`paragraph-${i}`}>{p}</p>
                  ))}
                </span>
              }
            />
            <SolutionBodyBottomSection
              coloredBackground={false}
              heading={content.useCases.heading}
              image={OrganizeWorkflowImage}
              paragraph={
                <div>
                  <p>{content.useCases.description}</p>
                  <ul>
                    {content.useCases.items.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              }
            />
          </div>
          <CallToActionBanner
            buttonText="Explore"
            heading={content.callToActionTitle}
            href={seeMoreHref}
            onClick={this.onSeeMoreClick}
          />
        </div>
      </MarketplaceAnalyticsContext>
    );
  }

  resolveSeeMoreHref() {
    const pathAndQuery = this.ctaPathAndQuery();
    return `${pathAndQuery.get('path')}${UrlUtils.resolveSearchFromImmutableQuery(
      pathAndQuery.get('query')
    )}`;
  }

  ctaPathAndQuery = () =>
    resolvePathAndQuery({
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      filters: this.props.solutionContent.getIn(['data', 'viewMoreFilters']),
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      links: this.props.addonDiscoveryConfig.links,
      toPerspective: perspectives.SEARCH,
      featureFlags: {
        // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
        enableJsdToJsm: this.props.enableJsdToJsm,
      },
    });

  updateLargeLinkSubjectID = (event: any) => event.update({ actionSubjectId: 'largeTileLink' });

  updateSmallLinkSubjectID = (event: any) => event.update({ actionSubjectId: 'smallTileLink' });

  onSeeMoreClick = (event: any) => {
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    analytics.trackSolutionsLoadMoreClick(this.props.solutionKey);
    LinkUtils.maybeOpenInNewTab(event, () => {
      const pathAndQuery = this.ctaPathAndQuery();
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      this.props.history.push({
        pathname: pathAndQuery.get('path'),
        search: UrlUtils.resolveSearchFromImmutableQuery(pathAndQuery.get('query')),
      });
    });
  };

  hitHrefResolver = ({ hit }) => {
    return resolveHitHref({
      appId: hit.get('addonId'),
      appName: hit.get('name'),
      // @ts-expect-error [MC-2850] - TS2345 - Argument of type '{ hit: any; preferredHosting: any; }' is not assignable to parameter of type '{ hit: any; filters: any; preferredHosting: any; }'.
      hosting: resolveHostingFromHit({
        hit,
        // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
        preferredHosting: this.props.solutionContent.getIn(['data', 'preferredHosting']),
      }),
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      generalLinks: this.props.generalLinks,
    });
  };

  hitImageHrefResolver = ({ hit }) => {
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    return resolveHitImageHref({ hit, generalLinks: this.props.generalLinks });
  };

  onHitClick = ({ hit }) => {
    navigateToAddon(
      {
        appId: hit.get('addonId'),
        appName: hit.get('name'),
        // @ts-expect-error [MC-2850] - TS2345 - Argument of type '{ hit: any; preferredHosting: any; }' is not assignable to parameter of type '{ hit: any; filters: any; preferredHosting: any; }'.
        hosting: resolveHostingFromHit({
          hit,
          // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
          preferredHosting: this.props.solutionContent.getIn(['data', 'preferredHosting']),
        }),
        // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
        generalLinks: this.props.generalLinks,
      },
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      this.props.history
    );
  };

  onHitMount = ({ hit, hitRank }) => {
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    analytics.trackSolutionsTileMounted(this.props.solutionKey, hit.get('addonKey'), hitRank);
  };

  onLargeCuratedHitMount = ({ hit, hitRank }) => {
    this.onHitMount({ hit, hitRank });
    analytics.trackSolutionsLargeCuratedTileMounted(
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      this.props.solutionKey,
      hit.get('addonKey'),
      hitRank
    );
  };

  onSmallCuratedHitMount = ({ hit, hitRank }) => {
    this.onHitMount({ hit, hitRank });
    analytics.trackSolutionsSmallCuratedTileMounted(
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      this.props.solutionKey,
      hit.get('addonKey'),
      hitRank
    );
  };

  addLargeCuratedAddonsToStaticSearchFilters = (params: any) =>
    this.addCuratedAppsToStaticSearchFiltersFactory(0, MAX_LARGE_CURATED_APP_COUNT)(params);

  addSmallCuratedAddonsToStaticSearchFilters = (params: any) =>
    this.addCuratedAppsToStaticSearchFiltersFactory(
      MAX_LARGE_CURATED_APP_COUNT,
      MAX_LARGE_CURATED_APP_COUNT + this.numSmallCuratedAppsForDisplay()
    )(params);

  /*
    A category might have very few apps, about 6 - 7 in all, to be listed.
    In this case we end up showing 2 small app tiles - which does not look good on the page.
    The solution is to show a row of apps only when it has 4 apps.
  */
  numSmallCuratedAppsForDisplay = () => {
    const appsForSmallTiles = this.state.curatedApps.slice(MAX_LARGE_CURATED_APP_COUNT);
    return numAppsForFullRows(appsForSmallTiles.size, SMALL_CURATED_APPS_PER_ROW);
  };

  addCuratedAppsToStaticSearchFiltersFactory = (start: any, end: any) => (params: any) =>
    this.state.curatedApps
      .slice(start, end)
      .reduce(
        (finalParams, key) => finalParams.addDisjunctiveFacetRefinement('addonKey', key),
        params
      );

  sortHits = (hits: any) =>
    hits.sort(
      (a, b) =>
        this.state.curatedApps.indexOf(a.get('addonKey')) -
        this.state.curatedApps.indexOf(b.get('addonKey'))
    );

  excludeCuratedAddonsFromStaticSearchResults = (params: any) =>
    this.state.curatedApps.reduce(
      (finalParams, key) => finalParams.addExcludeRefinement('addonKey', key),
      params
    );
}

export default withFeatureFlags(Solution);
